// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $Model,
} from 'common.data';

// Global Enums
import {
  webPages,
  webSections,
} from 'common.enums';
import {
  CrewmemberAccessTokenStatus,
} from 'common.data';

// Global Components
import * as React from 'react';

// Helpers
import moment from 'moment';
import ErrorLog from '@vroskus/library-sentry/dist/browser';
import {
  getDateTimeFormats,
} from 'common.helpers/dist/browser';

// Services
import Services from '../../../../services';

// Components
import Section from '../../../layout/Section';
import Loader from '../../../layout/Loader';
import ConfirmDialog from '../../../dialog/ConfirmDialog';
import NoData from '../../../common/NoData';

// Types
import type {
  $ReducerState,
} from '../../../../redux';
import type {
  $UnauthenticateAction,
} from '../../../../redux/modules/auth/actions';

export type $OwnProps = $Translate;

export type $StateProps = {
  auth: $ReducerState<'auth'>;
  main: $ReducerState<'main'>;
};

export type $DispatchProps = {
  unauthenticate: $UnauthenticateAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$Model<'CrewmemberAccessToken'>>;
  loading: boolean;
};

class SessionSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    try {
      await this.setStateAsync({
        loading: true,
      });

      const data = await Services.Api.getProfileSessionSectionData();

      await this.setStateAsync({
        data,
        loading: false,
      });
    } catch (error) {
      await this.setStateAsync({
        loading: false,
      });

      ErrorLog.exception(error as Error);
    }
  }

  setupSocket(): void {
    const {
      auth,
    } = this.props;

    Services.Socket.on({
      action: (data) => {
        if (auth.user
          && data.parentModelId === auth.user.id
          && data.modelName === 'CrewmemberAccessToken'
        ) {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  async unauthenticate(): Promise<void> {
    const {
      unauthenticate,
    } = this.props;

    try {
      await unauthenticate();
    } catch (err) {
      const error = err as Error;

      ErrorLog.exception(error);
    }
  }

  renderDataItem(
    dataItem: $Model<'CrewmemberAccessToken'>,
    dateTimeFormat: string,
  ) {
    const {
      t,
    } = this.props;

    const createdAtMoment = moment(dataItem.createdAt);
    const expMoment = moment(dataItem.Exp);

    return (
      <tr key={dataItem.id}>
        <td className={'flex flex-col md:flex-row text-xs md:text-base'}>
          <div className={'mr-2'}>
            <small className={'uppercase'}>
              {`${t('Device')}: `}
            </small>
            <span>
              {`${dataItem.Agent.platform} (${dataItem.Agent.os})`}
            </span>
          </div>
          <div className={'mr-2'}>
            <small className={'uppercase'}>
              {`${t('Browser')}: `}
            </small>
            <span>
              {dataItem.Agent.browser}
            </span>
          </div>
          <div className={'mr-2'}>
            <small className={'uppercase'}>
              {`${t('Ip')}: `}
            </small>
            <span>
              {dataItem.Agent.ip}
            </span>
          </div>
          {dataItem.Agent.country && (
            <div className={'mr-2'}>
              <small className={'uppercase'}>
                {`${t('Country')}: `}
              </small>
              <span>
                {dataItem.Agent.country}
              </span>
            </div>
          )}
          {dataItem.Agent.city && (
            <div className={'mr-2'}>
              <small className={'uppercase'}>
                {`${t('City')}: `}
              </small>
              <span>
                {dataItem.Agent.city}
              </span>
            </div>
          )}
        </td>
        <td className={'text-center'}>
          <div>{createdAtMoment.fromNow()}</div>
          <small>{createdAtMoment.format(dateTimeFormat)}</small>
        </td>
        <td className={'text-center'}>
          <div>{expMoment.fromNow()}</div>
          <small>{expMoment.format(dateTimeFormat)}</small>
        </td>
        <td className={'text-right'}>
          <button
            className={'btn btn_secondary btn_outlined uppercase px-2 md:px-3 py-1'}
            onClick={() => {
              try {
                Services.Api.invalidateAccessToken(dataItem);
              } catch (error) {
                ErrorLog.exception(error as Error);
              }
            }}
            type={'button'}
          >
            <em className={'la la-times text-2xl'} />
            <span className={'hidden md:block ml-2'}>
              {t('Invalidate')}
            </span>
          </button>
        </td>
      </tr>
    );
  }

  renderData() {
    const {
      main,
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    const formats = getDateTimeFormats({
      RegionId: main.RegionId,
      regions: main.regions,
    });

    const dateTimeFormat: string = `${formats.Date} ${formats.Time}`;

    const filteredData: Array<$Model<'CrewmemberAccessToken'>> = data.filter((
      dataItem: $Model<'CrewmemberAccessToken'>,
    ) => dataItem.Status === CrewmemberAccessTokenStatus.valid);

    return (
      <table className={'table table_hoverable w-full'}>
        <thead>
          <tr>
            <th className={'text-left uppercase'}>
              {t('Device data')}
            </th>
            <th className={'text-center uppercase'}>
              {t('Login date')}
            </th>
            <th className={'text-center uppercase'}>
              {t('Expires')}
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          {filteredData.map((
            dataItem: $Model<'CrewmemberAccessToken'>,
          ) => this.renderDataItem(
            dataItem,
            dateTimeFormat,
          ))}
        </tbody>
      </table>
    );
  }

  render() {
    const {
      t,
    } = this.props;
    const {
      data,
      loading,
    } = this.state;

    return (
      <Section id={webSections[webPages.profilePage].session}>
        <div className={'flex flex-row items-center mb-3'}>
          <h2 className={'h3 grow flex'}>
            {t('Session')}
          </h2>
          <div>
            <ConfirmDialog
              content={t('Do you really want to sign out?')}
              onConfirm={() => this.unauthenticate()}
              title={t('Confirmation needed')}
            >
              <button
                className={'btn btn_danger btn_outlined uppercase ml-auto'}
                type={'button'}
              >
                {t('Logout')}
              </button>
            </ConfirmDialog>
          </div>
        </div>
        <Loader
          className={'overflow-x-auto'}
          fade
          loading={loading === true}
        >
          {data.length > 0 ? this.renderData() : (
            <NoData label={t('The list is empty')} />
          )}
        </Loader>
      </Section>
    );
  }
}

export default SessionSectionView;
